import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { PlatformDatasetSummary } from "@tdelivr/api-client/generated/data-platform/data-catalog-lineage.js";

import { boundedNote } from "../board.ts";
import {
  DATA_CATALOGUE_SCOPE,
  REGISTER_REFUSAL,
  catalogueNote,
  datasetNames,
  datasetRow,
  statesPresent,
  stateOf,
} from "../data-catalogue.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `data` in `16 WP15 Platform Machinery.dc.html`: the data catalogue and its lineage.
 *
 * ```
 * data:['Data catalogue and lineage','DAT-03',
 *   'What each dataset came from and what it feeds, in both directions. Publishing needs an approver.',
 *   [['Datasets','84','done','published'],['Awaiting approval','1','human',''],
 *    ['Quality failures','2','caution','DAT-02'],['Lineage gaps','0','done','']],
 *   ['Dataset','Upstream','Downstream'],
 *   [['finance.settlements','ledger, payouts','reports, reconciliation','done','published'],
 *    ['ops.trips_daily','trip events','SLO board','caution','quality check failed'],
 *    ['risk.features','payments, devices','fraud model v9','human','awaiting approval']],
 *   'lock','Publishing reuses the approval pattern.']
 * ```
 *
 * What is drawn from data and what is not:
 *
 * | drawn                                    | source                                                   |
 * | ---------------------------------------- | -------------------------------------------------------- |
 * | the rows                                 | `DAT-03 GET /v1/platform/catalog/datasets` (ADR 0069)    |
 * | `Dataset` — `finance.settlements`        | `datasets.displayName`                                   |
 * | `Upstream` — `ledger, payouts`           | `datasets.upstreamDatasetIds`, named through this page    |
 * | `Downstream` — `reports, reconciliation` | `datasets.downstreamDatasetIds`, the same way            |
 * | the badge                                | `datasets.lifecycleState` — **all five words DAT-03's**  |
 * | `done · published`                       | **no such state.** ADR 0069                              |
 * | `human · awaiting approval`              | **unmodelled**: a version cannot exist without an approver |
 * | `caution · quality check failed`         | **DAT-02's**, which this page does not own                |
 * | the four KPI tiles                       | **nothing** — R-04, Q-178, and two of them are the above |
 * | `Register a dataset`                     | **drawn and refused.** `REGISTER_REFUSAL`, ADR 0069 §10  |
 *
 * ## One listing, every column filled, and every designed badge word wrong about this platform
 *
 * This is the inverse of `platform · Risk and fraud` one page over. There, two of four owners
 * published nothing by decision and two of three columns are empty; here the single owner published
 * a listing that fills all three columns — including both lineage cells, which ADR 0069 put **inside
 * the row** precisely so a board of N datasets with two lineage columns would not be 2N+1 calls —
 * and what has no source is the state vocabulary and three of the four tiles.
 *
 * `../data-catalogue.ts` carries the argument for each, and for the one join on this page: a lineage
 * identifier is resolved to a `displayName` by exact `(tenantId, datasetId)` match against the same
 * page's own rows, with the published URN as the fallback and the reason on the cell. No prefix rule,
 * no last-segment truncation.
 *
 * ## The approver is held and not published, and the page says so in the design's own words
 *
 * The purpose sentence is "Publishing needs an approver" and the closing note is "Publishing reuses
 * the approval pattern". Both are drawn word for word, and DAT-03 genuinely enforces that pattern at
 * three layers — `CHECK (requested_by <> approved_by)`, `publishDataset`'s refusal of the requester,
 * and `validateDatasetProposal`'s refusal of the equality again. **Neither name is on this page**:
 * two named staff subjects of every tenant on one board is an identity disclosure across the tenancy
 * boundary, and the design draws no column for either. ADR 0069 §5 is the section to argue with, and
 * it is the record's own words that the design's approval claim "is a purpose sentence and a `lock`
 * note, not a field".
 */

const dataCatalogue = services.data_platform__data_catalog_lineage;

export interface DataCatalogueProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Risk and data › Data catalogue and lineage`, the design's trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Data catalogue and lineage";
/** The design's own source chip, word for word: `DAT-03`. One owner, and it answers. */
const SOURCE = "DAT-03";
const DESCRIPTION =
  "What each dataset came from and what it feeds, in both directions. Publishing needs an approver.";
const COLUMNS = ["Dataset", "Upstream", "Downstream"] as const;
const NOTE = "Publishing reuses the approval pattern.";
/** The design's own `noteK` for this page is `lock`, which its `NK` map draws in the accent tint. */
const NOTE_KIND = "lock" as const;
/** The design's `FORMS.data` heading button, word for word. */
const PRIMARY_LABEL = "Register dataset";

/**
 * The most rows one request answers, which is DAT-03's own maximum.
 *
 * One request, and the bound matters more here than on a board whose rows stand alone: a lineage
 * identifier is named from the page in hand, so a page cut short leaves a cell drawing a URN for a
 * dataset the catalogue does in fact name. `catalogueNote` and the cell's own `title` both say which
 * values are identifiers rather than names, and `boundedNote` says when the page was cut.
 */
const PER_FETCH = 200;

export function DataCatalogue({ transport, session, crumbs }: DataCatalogueProps): ReactNode {
  const [rows, setRows] = useState<readonly PlatformDatasetSummary[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRows(undefined);
    setFailure(undefined);

    /*
     * Asked for before the call, not discovered from the refusal. ADR 0049 §3, and the notification
     * bell's three-console 403 is what happens without it: a refused read and an unreachable service
     * look identical from a browser.
     */
    if (!session.scopes.has(DATA_CATALOGUE_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [DATA_CATALOGUE_SCOPE] });
      return;
    }

    try {
      const client = dataCatalogue.createDataCatalogLineageClient(transport);
      /*
       * `listPlatformCatalogDatasets`, and the trap on this service is `currentDataset`.
       *
       * That read is `/internal/v1` with a `tenantId` and a `datasetId` on the path — it needs the
       * dataset URN this board exists to discover, which is `GET /v1/sessions`'s trap and ADR 0040's
       * arriving again — and it answers every field of a named dataset version under
       * `data-catalog:read`, requester, approver, location URN, grain prose and classification
       * included. ADR 0069 §1 left it byte-for-byte as it was.
       */
      const page = await client.listPlatformCatalogDatasets(String(PER_FETCH));
      setRows(page.datasets);
      /*
       * `nextAfter` is typed `unknown` by the generated client, which is a contract gap rather than
       * a property of the answer: the operation's inline response schema writes
       * `type: [string, "null"]` where the generator reads `nullable: true`, so the component schema
       * `PlatformDatasetCatalogue` types it `string | null` and the method does not. Compared against
       * both empty values rather than cast, because a cast would assert a shape this console cannot
       * see and the comparison is true of either spelling.
       */
      setTruncated(page.nextAfter !== null && page.nextAfter !== undefined);
    } catch (error) {
      setFailure(stateForError(error, [DATA_CATALOGUE_SCOPE]));
    }
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * The names every lineage cell resolves against, built over the **whole** answered page rather
   * than over the filtered rows.
   *
   * Filtering by state must not change what a cell says: a board narrowed to `certified` would
   * otherwise start drawing URNs for upstream datasets that are `experimental`, because the row
   * carrying their name had been filtered out of the map.
   */
  const names = datasetNames(rows ?? []);

  const boardRows =
    rows === undefined
      ? undefined
      : rows
          .filter((summary) => state === "" || stateOf(summary).label === state)
          .map((summary) => datasetRow(summary, names));

  const bounded = boundedNote(
    rows?.length,
    truncated,
    "datasets",
    "ordered by tenant, then dataset",
  );
  const span = catalogueNote(rows);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No dataset has been catalogued on any tenant. A row appears here when a dataset version is published, which needs a requester and a different approver."
      primary={{ label: PRIMARY_LABEL, refusal: REGISTER_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "lock", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(span === undefined ? {} : { footnote: span })}
      exportName="data-catalogue-lineage"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
